import { createServerFn } from "@tanstack/react-start";
import { createClient } from "./supabase/admin";
import { loadMenuCourseTagItemRows } from "./menu-course-tags";
import {
  getMenuPickOptionIds,
  getMenuPickProductIds,
  getMenuPicks,
  menuPicksSchema,
  normalizePickCount,
  selectOfferedMenuPicks,
} from "./menu-picks";
import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import z from "zod";
import { hasKnownEmailTld } from "@vrodex/utils/email-typo";
import {
  CouponAdjustmentTypes,
  CouponContext,
  CouponFilters,
  OrderLineTypes,
  OrderTypes,
  PaymentMethodContext,
  PaymentMethods,
  ProductTypes,
  ProductVisibilityTypes,
  Status,
  SupportedLanguages,
  Target,
  V2OptionType,
} from "./supabase/enums";
import { SalesChannels } from "./storefront/channel";
import { loadStorefrontConfiguration } from "./storefront/configuration";
import { loadAvailableStock } from "./storefront/stock";
import { toShippingMethod } from "./storefront/shipping";
import {
  getShippingFeeInCents,
  meetsMinimumOrder,
  shipsToCountry,
  ShippingMethodTypes,
  type ShippingMethod,
} from "./storefront/shipping-rules";
import {
  allocateOrderDiscount,
  calculatePaymentMethodFeeInCents,
  getMaxOrderDiscountInCents,
  menuItemPreparationName,
  splitMenuPrice,
} from "@vrodex/utils";
import { createMollieClient } from "@mollie/api-client";
import { Decimal } from "decimal.js";
import { PaymentMethod } from "@mollie/api-client";

import { mollie } from "./mollie";
import { formatNumberToCurrency, generateRandomString } from "./utils";
import { queryOptions } from "@tanstack/react-query";
import {
  format,
  getHours,
  getMinutes,
  isSameMinute,
  parseISO,
  startOfDay,
} from "date-fns";
import { waitUntil } from "@vercel/functions";

import { sendBroadcast } from "./supabase/client";
import { sendPushNotification } from "./push-notifications";
import { getServerT } from "./i18n/server-email";
import { supportedLanguages } from "./i18n/init";
import { getAddress } from "./postcode";
import { getAvailability } from "./shifts";
import { toZonedTime } from "date-fns-tz";
import { checkCoupon } from "./coupons";
import {
  linkCouponToOnlineOrder,
  syncCouponUsageForOnlineOrder,
} from "./coupon-usage";
import {
  buildOrderConfirmationLineData,
  sendOrderConfirmationEmail,
} from "./order-confirmation-email";

const calculateOrderDiscountAllocations = async ({
  orderId,
  discountValue,
  discountType,
}: {
  orderId: string;
  discountValue: number;
  discountType: CouponAdjustmentTypes;
}) => {
  const supabaseAdmin = createClient();

  const { data: applicableOrderLines, error: orderLinesError } =
    await supabaseAdmin
      .from("order_line")
      .update({
        order_discount_allocation_in_cents: 0,
      })
      .gt("quantity", 0)
      .eq("order_id", orderId)
      .in("type", [OrderLineTypes.product, OrderLineTypes.extra])
      .select(
        "id, price:new_price, oldPrice:price, quantity, discountInCents:discount_in_cents"
      );

  if (orderLinesError) {
    console.error("Could not get order lines", orderLinesError);
    throw new Error("Could not get order lines");
  }

  const lines = applicableOrderLines.map((line) => ({
    id: line.id,
    effectivePriceInCents:
      (line.price ?? line.oldPrice * 100) - line.discountInCents,
    quantity: line.quantity,
  }));

  const total = getMaxOrderDiscountInCents(lines);

  const discount =
    discountType === CouponAdjustmentTypes.fixed
      ? new Decimal(discountValue).toDecimalPlaces(0).toNumber()
      : new Decimal(total)
          .times(discountValue / 100)
          .toDecimalPlaces(0)
          .toNumber();

  // check if discount can be applied without making any item price negative
  if (discount > total) {
    console.error("Discount amount exceeds the total order value", {
      discount,
      maxPossibleDiscount: total,
    });

    throw new Error("Discount amount exceeds the total order value");
  }

  // Allocates in whole PER UNIT cents, the unit the column is read back in, so
  // the lines add up to the coupon amount instead of drifting a cent from it.
  const { perUnitInCentsByLineId, allocatedInCents } = allocateOrderDiscount({
    lines,
    discountInCents: discount,
  });

  // Update each eligible order line's order_discount_allocation_in_cents field (unit discount in cents)
  const updatePromises = [...perUnitInCentsByLineId].map(([id, perUnitCents]) =>
    supabaseAdmin
      .from("order_line")
      .update({
        order_discount_allocation_in_cents: perUnitCents,
      })
      .eq("id", id)
  );

  const [updateResults] = await Promise.all([Promise.all(updatePromises)]);

  const hasErrors = updateResults.some((result) => result.error);
  if (hasErrors) {
    console.error(
      "Failed to update some order lines",
      updateResults.filter((r) => r.error)
    );
    throw new Error("Failed to apply discount to all items");
  }

  // The amount the ORDER LINES were actually discounted, which is what the
  // customer is charged. Returning the requested amount instead let the Mollie
  // payment drift a cent away from the sum of the lines.
  return allocatedInCents;
};

const calculateProductDiscountAllocations = async ({
  orderId,
  discountValue,
  discountType,
  filterType,
  productIds,
  categoryIds,
  productMap,
}: {
  orderId: string;
  discountValue: number;
  discountType: CouponAdjustmentTypes;
  filterType: CouponFilters;
  productIds?: string[];
  categoryIds?: string[];
  productMap: Map<
    string,
    {
      product: any;
      orderItem: any;
      quantity: number;
      categoryName?: string;
      comment?: string;
      turnoverGroupName?: string;
      vatRate: number;
      selectedOptions: Array<{
        productOptionId: string;
        productOptionName: string;
        productOptionPrice: number;
      }>;
      price: () => number;
    }
  >;
}) => {
  const supabaseAdmin = createClient();

  // Get all product order lines for this order
  const { data: allOrderLines, error: orderLinesError } = await supabaseAdmin
    .from("order_line")
    .update({
      order_discount_allocation_in_cents: 0,
    })
    .gt("quantity", 0)
    .eq("order_id", orderId)
    .in("type", [OrderLineTypes.product, OrderLineTypes.extra])
    .select(
      "id, price:new_price, oldPrice:price, quantity, discountInCents:discount_in_cents, name, display_name, parent_id"
    );

  if (orderLinesError) {
    console.error("Could not get order lines", orderLinesError);
    throw new Error("Could not get order lines");
  }

  if (!allOrderLines || allOrderLines.length === 0) {
    return 0;
  }

  // Create a map from product name/display_name to product info from productMap
  const productNameToInfoMap = new Map<
    string,
    {
      productId: string;
      categoryId?: string;
      categoryName?: string;
    }
  >();

  productMap.forEach((entry) => {
    const name = entry.product.display_name || entry.product.name;
    productNameToInfoMap.set(name, {
      productId: entry.product.id,
      categoryId:
        entry.product.product_category_id || entry.product.category?.id,
      categoryName: entry.categoryName,
    });
  });

  // Filter order lines based on filter_type
  let eligibleOrderLines = allOrderLines;

  if (filterType === CouponFilters.selected_products && productIds) {
    eligibleOrderLines = allOrderLines.filter((line) => {
      const productInfo = productNameToInfoMap.get(
        line.display_name || line.name
      );
      return productInfo && productIds.includes(productInfo.productId);
    });
  } else if (filterType === CouponFilters.products_in_category && categoryIds) {
    eligibleOrderLines = allOrderLines.filter((line) => {
      const productInfo = productNameToInfoMap.get(
        line.display_name || line.name
      );
      return (
        productInfo &&
        productInfo.categoryId &&
        categoryIds.includes(productInfo.categoryId)
      );
    });
  }
  // If filterType is all_products, eligibleOrderLines already contains all lines

  if (eligibleOrderLines.length === 0) {
    return 0;
  }

  // Get IDs of eligible order lines (main products)
  const eligibleOrderLineIds = new Set(
    eligibleOrderLines.map((line) => line.id)
  );

  // Find all child order lines (those with parent_id matching eligible main products)
  const childOrderLines = allOrderLines.filter(
    (line) => line.parent_id && eligibleOrderLineIds.has(line.parent_id)
  );

  // Combine eligible main products with their children
  const allEligibleOrderLines = [...eligibleOrderLines, ...childOrderLines];

  const lines = allEligibleOrderLines.map((line) => ({
    id: line.id,
    effectivePriceInCents:
      (line.price ?? line.oldPrice * 100) - line.discountInCents,
    quantity: line.quantity,
  }));

  const total = getMaxOrderDiscountInCents(lines);

  if (total === 0) {
    return 0;
  }

  const discount =
    discountType === CouponAdjustmentTypes.fixed
      ? new Decimal(discountValue).toDecimalPlaces(0).toNumber()
      : new Decimal(total)
          .times(discountValue / 100)
          .toDecimalPlaces(0)
          .toNumber();

  // check if discount can be applied without making any item price negative
  if (discount > total) {
    console.error("Discount amount exceeds the total eligible product value", {
      discount,
      maxPossibleDiscount: total,
    });

    throw new Error("Discount amount exceeds the total eligible product value");
  }

  const { perUnitInCentsByLineId, allocatedInCents } = allocateOrderDiscount({
    lines,
    discountInCents: discount,
  });

  // Update each eligible order line's order_discount_allocation_in_cents field (unit discount in cents)
  const updatePromises = [...perUnitInCentsByLineId].map(([id, perUnitCents]) =>
    supabaseAdmin
      .from("order_line")
      .update({
        order_discount_allocation_in_cents: perUnitCents,
      })
      .eq("id", id)
  );

  const [updateResults] = await Promise.all([Promise.all(updatePromises)]);

  const hasErrors = updateResults.some((result) => result.error);
  if (hasErrors) {
    console.error(
      "Failed to update some order lines",
      updateResults.filter((r) => r.error)
    );
    throw new Error("Failed to apply discount to all items");
  }

  // The amount the ORDER LINES were actually discounted; see the note in
  // calculateOrderDiscountAllocations above.
  return allocatedInCents;
};

export const createOrderSchema = z
  .object({
    items: z.array(
      z.object({
        id: z.string().uuid(),
        productId: z.string().uuid(),
        comment: z.string().optional(),
        quantity: z.number(),
        selectedOptions: z.record(z.string(), z.string()).optional(),
        // Menu products only: the picks per course (courseId -> [{ productId,
        // optionSelections }]), one order line each. Prices, supplements and
        // the course's pick limit are always resolved server-side.
        menuPicks: menuPicksSchema.optional(),
        // Legacy menu shape (carts saved before menu picks, older bundles):
        // one chosen product per course (courseId -> productId) and its
        // options (courseId -> { subgroupId -> optionId(s) }). Ignored when
        // menuPicks is sent.
        menuSelections: z.record(z.string(), z.string()).optional(),
        menuOptionSelections: z
          .record(z.string(), z.record(z.string(), z.string()))
          .optional(),
      })
    ),
    language: z.nativeEnum(SupportedLanguages),
    customer: z.object({
      firstName: z.string().min(1),
      lastName: z.string().optional(),
      phone: z.string().min(1),
      // `gmail.con` passes the format check but never gets the order mails.
      email: z.string().email().refine(hasKnownEmailTld),
    }),
    timeslot: z.date(),
    address: z
      .object({
        streetName: z.string().min(1),
        streetNumber: z.string(),
        zipCode: z.string(),
        city: z.string(),
        latitude: z.number().optional(),
        longitude: z.number().optional(),
        // Storefront only: second address line and ISO 3166-1 alpha-2 country.
        addition: z.string().max(120).optional(),
        country: z
          .string()
          .regex(/^[A-Za-z]{2}$/)
          .transform((value) => value.toUpperCase())
          .optional(),
      })
      .optional(),
    // Storefront only: the chosen shipping or pickup method. Its type decides
    // the order type (shipping = delivery, pickup = pickup).
    shippingMethodId: z.string().uuid().optional(),
    payment: z.object({
      paymentMethodId: z.string(),
      paysWith: z.string().optional(),
    }),
    orderType: z.nativeEnum(OrderTypes),
    requestedTime: z
      .string()
      .optional()
      .transform((val) => (val === "ASAP" ? undefined : val)),
    comment: z.string().optional(),
    coupon: z
      .object({
        code: z.string(),
        coupon: z.any().optional(),
      })
      .optional(),
    sessionId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.orderType === OrderTypes.delivery && !data.address) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address"],
        message: "Address is required for delivery orders",
      });
    }

    if (data.orderType === OrderTypes.pickup) {
      // Clear address for pickup orders
      data.address = undefined;
    }
  });

export const createOrder = createServerFn({ method: "POST" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(createOrderSchema)
  .handler(
    async ({
      context: {
        userId,
        origin,
        isQrLinkHost,
        channel,
        onlineOrderConfigurationId,
      },
      data,
    }) => {
    // The stable QR host serves a merchant without a domain for ordering at the
    // table only; their web shop is not open until they have a domain.
    if (isQrLinkHost) {
      return {
        success: false,
        code: "general_error",
      };
    }

    // A storefront (webshop) order ships a parcel or is collected at the shop.
    // It skips the restaurant's time slots and delivery areas, is priced from
    // the webshop price list, sells from stock and is paid online only.
    const isStorefront = channel === SalesChannels.storefront;

    if (isStorefront && !data.shippingMethodId) {
      return { success: false, code: "shipping_method_unavailable" };
    }

    const supabase = createClient();
    const generatedReference = `vr_${generateRandomString(7)}`;
    const generatedSecret = generateRandomString(10);

    const requestedTimeInTimezone = data.requestedTime
      ? toZonedTime(data.requestedTime, "Europe/Amsterdam")
      : null;

    let orderNumber = 1;

    // A storefront prices both order types from its one price list.
    const onlineOrderConfigurationPromise = isStorefront
      ? loadStorefrontConfiguration(
          supabase,
          userId,
          onlineOrderConfigurationId
        ).then((result) =>
          result.data
            ? {
                data: {
                  delivery_price_id: result.data.priceId,
                  take_away_price_id: result.data.priceId,
                  mollieProfileId: result.data.mollieProfileId,
                },
                error: null,
              }
            : { data: null, error: result.error }
        )
      : supabase
          .from("online_order_configuration")
          .select(
            "delivery_price_id, take_away_price_id, mollieProfileId:mollie_profile_id"
          )
          .eq("is_default", true)
          .eq("user_id", userId)
          .single();

    // first verify all products and get their price
    const productsPromise = supabase
      .from("product")
      .select(
        "id, type, name, display_name, purchase_price_in_cents, available_from_time, available_until_time, product_category_id, visibility_types_new, is_modifier, split_menu_price_by_vat, stock_managed, current_stock, stock_source_product_id, units_per_source_product, turnoverGroup:turnover_group(name), vatRate:vat_rate(value), prices:price_on_product(priceId:price_id, newValue:new_value, oldValue:value), category:product_category(id, name, available_from_time, available_until_time, target, vatRate:vat_rate(value), turnoverGroup:turnover_group(name))"
      )
      .in(
        "id",
        data.items.map((item) => item.productId)
      )
      .eq("user_id", userId)
      .eq("status", Status.active);

    // Get all selected option IDs from the order items, including the options
    // chosen on menu picks so their definitions and overrides are fetched too.
    const selectedOptionIds = data.items.flatMap((item) => [
      ...Object.values(item.selectedOptions ?? {}).flatMap((value) =>
        value.split(",").map((id) => id.trim())
      ),
      ...getMenuPickOptionIds(item),
    ]);
    const uniqueSelectedOptionIds = Array.from(
      new Set(selectedOptionIds.filter(Boolean))
    );
    // Menu course items are products in their own right; include them so their
    // product-level option overrides are fetched.
    const menuChildProductIdsFromItems = data.items.flatMap((item) =>
      getMenuPickProductIds(item)
    );
    const selectedProductIds = Array.from(
      new Set([
        ...data.items.map((item) => item.productId),
        ...menuChildProductIdsFromItems,
      ])
    );

    const productOptionsPromise =
      uniqueSelectedOptionIds.length > 0
        ? supabase
            .from("v2_option")
            .select(
              "id, name, price_in_cents, use_product_price, type, product_id, option_group_id, prep_only, hide_on_prep"
            )
            .in("id", uniqueSelectedOptionIds)
            .eq("status", Status.active)
            .in("type", [V2OptionType.single, V2OptionType.product])
        : Promise.resolve({ data: [], error: null });

    const categoryOptionOverridesPromise =
      uniqueSelectedOptionIds.length > 0
        ? supabase
            .from("v2_category_option_override")
            .select(
              "category_id, option_id, is_enabled, price_override_in_cents"
            )
            .in("option_id", uniqueSelectedOptionIds)
        : Promise.resolve({ data: [], error: null });

    const productOptionOverridesPromise =
      uniqueSelectedOptionIds.length > 0 && selectedProductIds.length > 0
        ? supabase
            .from("v2_product_option_override")
            .select(
              "product_id, option_id, is_enabled, price_override_in_cents"
            )
            .in("product_id", selectedProductIds)
            .in("option_id", uniqueSelectedOptionIds)
        : Promise.resolve({ data: [], error: null });

    const latestOrderPromise = supabase
      .from("online_order")
      .select("number")
      .eq("user_id", userId)
      .not("number", "is", null)
      .neq("order_status", "pending_payment")
      .gte("created_at", startOfDay(new Date()).toISOString())
      .order("number", { ascending: false })
      .limit(1)
      .maybeSingle();

    // Get today's date in YYYY-MM-DD format (in the timezone)
    const now = new Date();
    const nowInTimezone = toZonedTime(now, "Europe/Amsterdam");
    const todayDateString = format(nowInTimezone, "yyyy-MM-dd");

    // Fetch product day availability overrides active as of today (an override can span
    // multiple days or be indefinite, so fetch all anchored on/before today, latest first).
    const productDayAvailabilityPromise = supabase
      .from("product_day_availability")
      .select("product_id, is_available, date, unavailable_until")
      .lte("date", todayDateString)
      .order("date", { ascending: false })
      .in(
        "product_id",
        data.items.map((item) => item.productId)
      );

    const [
      onlineOrderConfigurationResult,
      productsResult,
      availabilityResult,
      latestOrderResult,
      productOptionsResult,
      categoryOptionOverridesResult,
      productOptionOverridesResult,
      productDayAvailabilityResult,
    ] = await Promise.all([
      onlineOrderConfigurationPromise,
      productsPromise,
      // A storefront order has no time slot to validate.
      isStorefront
        ? Promise.resolve({ success: true as const, data: [] })
        : getAvailability({
            data: { date: data.timeslot ?? new Date(), type: data.orderType },
          }),
      latestOrderPromise,
      productOptionsPromise,
      categoryOptionOverridesPromise,
      productOptionOverridesPromise,
      productDayAvailabilityPromise,
    ]);

    if (latestOrderResult.error) {
      console.error("Could not get latest order", latestOrderResult.error);
      return {
        success: false,
        code: "general_error",
      };
    }

    if (productOptionsResult.error) {
      console.error(
        "Could not get product options",
        productOptionsResult.error
      );
      return {
        success: false,
        code: "general_error",
      };
    }

    if (categoryOptionOverridesResult.error) {
      console.error(
        "Could not get category option overrides",
        categoryOptionOverridesResult.error
      );
      return {
        success: false,
        code: "general_error",
      };
    }

    if (productOptionOverridesResult.error) {
      console.error(
        "Could not get product option overrides",
        productOptionOverridesResult.error
      );
      return {
        success: false,
        code: "general_error",
      };
    }

    if (productDayAvailabilityResult.error) {
      console.error(
        "Could not get product day availability",
        productDayAvailabilityResult.error
      );
      return {
        success: false,
        code: "general_error",
      };
    }

    const latestOrderToday = latestOrderResult.data;

    if (latestOrderToday?.number) {
      orderNumber = latestOrderToday.number + 1;
    }

    if (onlineOrderConfigurationResult.error) {
      console.error(
        "Could not get online order configuration",
        onlineOrderConfigurationResult.error
      );
      return {
        success: false,
        code: "general_error",
      };
    }

    if (productsResult.error) {
      console.error("Could not get products", productsResult.error);
      return {
        success: false,
        code: "general_error",
      };
    }

    if (availabilityResult.success === false) {
      console.error("Could not get availability", availabilityResult.message);
      return {
        success: false,
        code: "general_error",
      };
    }

    // Check if all products are available for the chosen order type
    const products = productsResult.data;
    // The storefront sells what is on the webshop, whichever way it leaves.
    const requiredVisibilityType = isStorefront
      ? ProductVisibilityTypes.webshop
      : data.orderType === OrderTypes.delivery
        ? ProductVisibilityTypes.delivery
        : ProductVisibilityTypes.pickup;
    const requiredTargetType = isStorefront
      ? Target.webshop
      : data.orderType === OrderTypes.delivery
        ? Target.delivery
        : Target.pickup;

    const unavailableProducts = products.filter((product) => {
      // A modifier product is only sold inside an option group, never as a
      // line of its own.
      if (product.is_modifier) return true;

      // Set menus are a restaurant's; the storefront never lists them.
      if (isStorefront && product.type === ProductTypes.menu) return true;

      // Empty/null visibility now means the product is hidden from every order
      // type, so a product is blocked unless it explicitly includes this type.
      const visibility = product.visibility_types_new ?? [];
      const productBlocked = !visibility.includes(requiredVisibilityType);

      // Empty/null category target means the category is available for all order types
      const categoryTarget = product.category?.target ?? [];
      const categoryBlocked =
        categoryTarget.length > 0 &&
        !categoryTarget.includes(requiredTargetType);

      return productBlocked || categoryBlocked;
    });

    if (unavailableProducts.length > 0) {
      // Map unavailable products to their order items to get quantities
      const unavailableProductIds = new Set(
        unavailableProducts.map((p) => p.id)
      );
      const unavailableOrderItems = data.items.filter((item) =>
        unavailableProductIds.has(item.productId)
      );

      return {
        success: false,
        code: "products_not_available_for_order_type",
        productNames: unavailableProducts.map((p) => p.display_name ?? p.name),
        unavailableItems: unavailableOrderItems.map((item) => ({
          productId: item.productId,
          productName:
            unavailableProducts.find((p) => p.id === item.productId)
              ?.display_name ??
            unavailableProducts.find((p) => p.id === item.productId)?.name ??
            "",
          quantity: item.quantity,
        })),
      };
    }

    // Collapse day-availability rows (ordered date DESC) into the effective availability of
    // the override active right now, per product. An "unavailable" override stays active until
    // `unavailable_until` (null = indefinite); an "available" (force-show) override only applies
    // to today. Products with no active override are absent from the map.
    const productAvailabilityMap = new Map<string, boolean>();
    productDayAvailabilityResult.data.forEach((availability) => {
      if (productAvailabilityMap.has(availability.product_id)) return;
      if (availability.is_available === false) {
        if (
          availability.unavailable_until === null ||
          new Date(availability.unavailable_until) > now
        ) {
          productAvailabilityMap.set(availability.product_id, false);
        }
        // expired unavailable override — keep scanning older rows for this product
        return;
      }
      if (availability.date === todayDateString) {
        productAvailabilityMap.set(availability.product_id, true);
      }
    });

    const unavailableProductsToday = products.filter((product) => {
      // If no record exists, product is available (defaults to true)
      if (!productAvailabilityMap.has(product.id)) {
        return false;
      }
      // Check if product is marked as unavailable
      return productAvailabilityMap.get(product.id) === false;
    });

    if (unavailableProductsToday.length > 0) {
      return {
        success: false,
        code: "products_temporarily_unavailable",
        productNames: unavailableProductsToday.map(
          (p) => p.display_name ?? p.name
        ),
      };
    }

    // Nothing may be sold that is not on the shelf. Checked again here, not
    // only in the cart, because stock moves while the customer shops; it is
    // taken off when the payment comes in (see the Mollie webhook).
    if (isStorefront) {
      const availableStock = await loadAvailableStock(
        supabase,
        userId,
        products.map((product) => ({
          id: product.id,
          stockManaged: product.stock_managed,
          currentStock: product.current_stock,
          stockSourceProductId: product.stock_source_product_id,
          unitsPerSourceProduct: product.units_per_source_product,
        }))
      );

      const requestedByProductId = new Map<string, number>();
      data.items.forEach((item) => {
        requestedByProductId.set(
          item.productId,
          (requestedByProductId.get(item.productId) ?? 0) + item.quantity
        );
      });

      const shortProducts = products.filter((product) => {
        const available = availableStock.get(product.id);
        return (
          available !== undefined &&
          (requestedByProductId.get(product.id) ?? 0) > available
        );
      });

      if (shortProducts.length > 0) {
        return {
          success: false,
          code: "insufficient_stock",
          productNames: shortProducts.map((p) => p.display_name ?? p.name),
          stockItems: shortProducts.map((product) => ({
            productId: product.id,
            productName: product.display_name ?? product.name,
            available: availableStock.get(product.id) ?? 0,
          })),
        };
      }
    }

    const availability = availabilityResult.data ?? [];

    // Availability is recomputed here on purpose: the customer may have been
    // sitting on the checkout page long enough for their slot to fall inside
    // the kitchen's preparation window, and that time may no longer be ordered.
    const selectedSlot = isStorefront
      ? undefined
      : requestedTimeInTimezone
        ? availability.find((a) => {
            const slotInTimezone = toZonedTime(a.start, "Europe/Amsterdam");
            return isSameMinute(requestedTimeInTimezone, slotInTimezone);
          })
        : availability.find((a) => a.type === data.orderType && a.spots > 0);

    if (!isStorefront && (!selectedSlot || selectedSlot.spots === 0)) {
      return {
        success: false,
        code: requestedTimeInTimezone
          ? "timeslot_no_longer_available"
          : "order_type_not_available",
      };
    }

    // The moment the order is actually handed over. Taken from the server
    // validated slot rather than the client's timeslot, which can be an older
    // ASAP slot that has since moved inside the preparation window. A
    // storefront order is handed over to the shop the moment it is placed.
    const slotStart = selectedSlot ? new Date(selectedSlot.start) : new Date();

    const onlineOrderConfiguration = onlineOrderConfigurationResult.data;

    // Fetch linked products for product-type options (options linked to a product)
    const linkedProductIds = [
      ...new Set(
        (productOptionsResult.data ?? [])
          .filter(
            (o) => o.type === V2OptionType.product && o.product_id != null
          )
          .map((o) => o.product_id!)
      ),
    ];
    const linkedProductsMap = new Map<
      string,
      {
        displayName: string;
        name: string;
        takeAwayPriceInCents: number;
        deliveryPriceInCents: number;
      }
    >();
    if (
      linkedProductIds.length > 0 &&
      onlineOrderConfiguration != null
    ) {
      const linkedProductsResult = await supabase
        .from("product")
        .select(
          "id, name, display_name, prices:price_on_product(priceId:price_id, newValue:new_value, oldValue:value)"
        )
        .in("id", linkedProductIds)
        .eq("type", ProductTypes.product)
        .eq("user_id", userId)
        .neq("status", Status.trashed);

      if (!linkedProductsResult.error && linkedProductsResult.data) {
        const takeAwayPriceId =
          onlineOrderConfiguration.take_away_price_id;
        const deliveryPriceId =
          onlineOrderConfiguration.delivery_price_id;
        linkedProductsResult.data.forEach((product: any) => {
          const takeAwayPrice = product.prices?.find(
            (p: { priceId: string }) => p.priceId === takeAwayPriceId
          );
          const deliveryPrice = product.prices?.find(
            (p: { priceId: string }) => p.priceId === deliveryPriceId
          );
          linkedProductsMap.set(product.id, {
            displayName: product.display_name ?? product.name ?? "",
            name: product.name ?? "",
            takeAwayPriceInCents:
              takeAwayPrice?.newValue ?? takeAwayPrice?.oldValue ?? 0,
            deliveryPriceInCents:
              deliveryPrice?.newValue ?? deliveryPrice?.oldValue ?? 0,
          });
        });
      }
    }

    if (products.map((product) => product.id).some((id) => !id)) {
      console.warn("Some products are not active", products, data.items);
      return { success: false, code: "products_are_not_active" };
    }

    // Check whether the selected timeslot falls within each product's (and its
    // category's) daily availability window. We compare only the time portion,
    // ignoring the date, and apply the strictest of product/category at each
    // bound: latest "from" and earliest "until".
    const referenceInTimezone = toZonedTime(slotStart, "Europe/Amsterdam");
    const referenceMinutes =
      getHours(referenceInTimezone) * 60 + getMinutes(referenceInTimezone);

    const toMinutesOfDay = (timeString: string): number => {
      const inTimezone = toZonedTime(parseISO(timeString), "Europe/Amsterdam");
      return getHours(inTimezone) * 60 + getMinutes(inTimezone);
    };

    const productsBeforeFrom: typeof products = [];
    const productsWithExpiredTime: typeof products = [];

    // A parcel has no time of day, so the storefront ignores serving hours.
    (isStorefront ? [] : products).forEach((product) => {
      const fromCandidates: number[] = [];
      if (product.available_from_time) {
        fromCandidates.push(toMinutesOfDay(product.available_from_time));
      }
      if (product.category?.available_from_time) {
        fromCandidates.push(
          toMinutesOfDay(product.category.available_from_time)
        );
      }

      const untilCandidates: number[] = [];
      if (product.available_until_time) {
        untilCandidates.push(toMinutesOfDay(product.available_until_time));
      }
      if (product.category?.available_until_time) {
        untilCandidates.push(
          toMinutesOfDay(product.category.available_until_time)
        );
      }

      if (fromCandidates.length > 0) {
        const effectiveFrom = Math.max(...fromCandidates);
        if (referenceMinutes < effectiveFrom) {
          productsBeforeFrom.push(product);
          return;
        }
      }

      if (untilCandidates.length > 0) {
        const effectiveUntil = Math.min(...untilCandidates);
        if (referenceMinutes > effectiveUntil) {
          productsWithExpiredTime.push(product);
        }
      }
    });

    const orderedProductIds = new Set(data.items.map((item) => item.productId));

    const orderedProductsBeforeFrom = productsBeforeFrom.filter((p) =>
      orderedProductIds.has(p.id)
    );
    if (orderedProductsBeforeFrom.length > 0) {
      return {
        success: false,
        code: "product_not_yet_available",
        productNames: orderedProductsBeforeFrom.map(
          (p) => p.display_name ?? p.name
        ),
      };
    }

    const orderedProductsExpired = productsWithExpiredTime.filter((p) =>
      orderedProductIds.has(p.id)
    );
    if (orderedProductsExpired.length > 0) {
      return {
        success: false,
        code: "product_no_longer_available",
        productNames: orderedProductsExpired.map(
          (p) => p.display_name ?? p.name
        ),
      };
    }

    const productMap = new Map<
      string,
      {
        product: (typeof products)[number];
        orderItem: (typeof data.items)[number];
        quantity: number;
        price: () => number;
        comment?: string;
        categoryName?: string;
        turnoverGroupName?: string;
        vatRate: number;
        selectedOptions: {
          productOptionId: string;
          productOptionName: string;
          productOptionPrice: number;
          // The v2 option group the option belongs to; the KDS colors the
          // extra by it.
          optionGroupId: string | null;
          // Kitchen-only option: kept off the customer receipt.
          prepOnly: boolean;
          // Receipt-only option: kept off the kitchen display.
          hideOnPrep: boolean;
        }[];
      }
    >();

    const productsMap = new Map<string, (typeof products)[number]>();

    const productOptionsMap = new Map<
      string,
      (typeof productOptionsResult.data)[number]
    >();
    if (productOptionsResult.data) {
      productOptionsResult.data.forEach((productOption) => {
        productOptionsMap.set(productOption.id, productOption);
      });
    }

    const categoryOverridesByCategoryAndOption = new Map<
      string,
      Map<string, (typeof categoryOptionOverridesResult.data)[number]>
    >();
    if (categoryOptionOverridesResult.data) {
      categoryOptionOverridesResult.data.forEach((override) => {
        if (!categoryOverridesByCategoryAndOption.has(override.category_id)) {
          categoryOverridesByCategoryAndOption.set(
            override.category_id,
            new Map()
          );
        }
        categoryOverridesByCategoryAndOption
          .get(override.category_id)!
          .set(override.option_id, override);
      });
    }

    const productOverridesByProductAndOption = new Map<
      string,
      Map<string, (typeof productOptionOverridesResult.data)[number]>
    >();
    if (productOptionOverridesResult.data) {
      productOptionOverridesResult.data.forEach((override) => {
        if (!productOverridesByProductAndOption.has(override.product_id)) {
          productOverridesByProductAndOption.set(
            override.product_id,
            new Map()
          );
        }
        productOverridesByProductAndOption
          .get(override.product_id)!
          .set(override.option_id, override);
      });
    }

    products.forEach((product) => {
      productsMap.set(product.id, product);
    });

    // Resolve a set of selected options (subgroupId -> optionId(s)) into priced
    // order-line data for a given owner product. Price cascade: product
    // override > category override > base option / linked-product price. Shared
    // by top-level products and menu course items so both price identically.
    const resolveSelectedOptionLines = (
      ownerProductId: string,
      ownerCategoryId: string | null,
      selectedOptions: Record<string, string> | undefined
    ) =>
      Object.entries(selectedOptions ?? {}).flatMap(([key, value]) => {
        // Handle comma-separated option IDs (for optional checkboxes)
        const optionIds = value
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);
        return optionIds.map((optionId) => {
          const productOption = productOptionsMap.get(optionId);

          const categoryId = ownerCategoryId;

          const productOverrides =
            productOverridesByProductAndOption.get(ownerProductId);
          const categoryOverrides = categoryId
            ? categoryOverridesByCategoryAndOption.get(categoryId)
            : undefined;

          // Resolve price: product override > category override > base option/linked product price
          let optionPriceInCents = productOption?.price_in_cents ?? 0;
          let optionName = productOption?.name ?? "";

          // When useProductPrice is on for a product-type option, snapshot
          // the linked product's current price and ignore option price and
          // overrides entirely.
          const useProductPrice =
            productOption?.type === V2OptionType.product &&
            productOption?.product_id &&
            productOption?.use_product_price === true;

          // Options linked to a product: use linked product for name and optionally price
          if (
            productOption?.type === V2OptionType.product &&
            productOption.product_id
          ) {
            const linkedProduct = linkedProductsMap.get(
              productOption.product_id
            );
            if (linkedProduct) {
              optionName = linkedProduct.displayName || linkedProduct.name;
              if (useProductPrice) {
                // Snapshot live product price for the order type at the moment of order creation.
                optionPriceInCents =
                  data.orderType === OrderTypes.pickup
                    ? linkedProduct.takeAwayPriceInCents
                    : linkedProduct.deliveryPriceInCents;
              } else {
                // If no override: use option price when set (including 0); only when null/undefined use linked product price
                const hasOverride =
                  productOverrides?.has(optionId) &&
                  productOverrides.get(optionId)!.price_override_in_cents !==
                    null;
                const hasCategoryOverride =
                  categoryOverrides?.has(optionId) &&
                  categoryOverrides.get(optionId)!.price_override_in_cents !==
                    null;
                if (!hasOverride && !hasCategoryOverride) {
                  if (productOption.price_in_cents != null) {
                    // Option price is set (including 0) — use it
                    optionPriceInCents = productOption.price_in_cents;
                  } else {
                    // Option has no price set — use linked product price for order type
                    optionPriceInCents =
                      data.orderType === OrderTypes.pickup
                        ? linkedProduct.takeAwayPriceInCents
                        : linkedProduct.deliveryPriceInCents;
                  }
                }
              }
            }
          }

          // Overrides do not apply when useProductPrice is on.
          if (!useProductPrice) {
            if (productOverrides?.has(optionId)) {
              const override = productOverrides.get(optionId)!;
              if (override.price_override_in_cents !== null) {
                optionPriceInCents = override.price_override_in_cents;
              }
            } else if (categoryOverrides?.has(optionId)) {
              const override = categoryOverrides.get(optionId)!;
              if (override.price_override_in_cents !== null) {
                optionPriceInCents = override.price_override_in_cents;
              }
            }
          }

          // A prep-only option never reaches the receipt, so no override may
          // put a charge on it.
          const prepOnly = productOption?.prep_only ?? false;

          return {
            productOptionId: key,
            productOptionName: optionName,
            productOptionPrice: prepOnly ? 0 : optionPriceInCents,
            optionGroupId: productOption?.option_group_id ?? null,
            prepOnly,
            hideOnPrep: productOption?.hide_on_prep ?? false,
          };
        });
      });

    data.items.forEach((item) => {
      const product = productsMap.get(item.productId);
      if (product) {
        productMap.set(item.id, {
          product: product,
          orderItem: item,
          quantity: item.quantity,
          categoryName: product.category?.name,
          comment: item.comment,
          turnoverGroupName:
            product.turnoverGroup?.name ??
            product.category?.turnoverGroup?.name,
          vatRate:
            product.vatRate?.value ?? product.category?.vatRate?.value ?? 0,
          selectedOptions: resolveSelectedOptionLines(
            product.id,
            product.product_category_id ?? product.category?.id ?? null,
            item.selectedOptions
          ),
          price: () => {
            const price =
              data.orderType === OrderTypes.pickup
                ? product.prices.find(
                    (price: any) =>
                      price.priceId ===
                      onlineOrderConfiguration.take_away_price_id
                  )
                : product.prices.find(
                    (price: any) =>
                      price.priceId ===
                      onlineOrderConfiguration.delivery_price_id
                  );
            const unitPrice = price?.newValue
              ? new Decimal(price.newValue).div(100).toNumber()
              : price?.oldValue ?? 0;
            return unitPrice;
          },
        });
      }
    });

    // --- Menu products: resolve the chosen course items into child lines ---
    // A menu product carries a fixed base price; each selected course item may
    // add a supplement. Supplements and names are resolved from the DB here,
    // never trusted from the client. The child lines are built after the parent
    // menu line is inserted (below) so they can reference its order_line id.
    interface MenuChildLineSpec {
      productId: string;
      name: string;
      // What tickets and the KDS print instead of the name: the item's
      // preparation name plus its course's suffix, if either is set.
      preparationName: string | null;
      courseId: string;
      courseName: string | null;
      priceSupplementInCents: number;
      vatRate: number;
      // With the menu's "split the menu price by VAT" on: this item's share of
      // the menu price, per unit. null = the menu line keeps its price.
      menuPriceShareInCents: number | null;
      // Cost of this course item at order time. The menu's own line carries no
      // cost (it is a container priced as a whole), so the margin on a menu is
      // the sum of what its course items cost.
      purchasePriceInCents: number | null;
      // Options chosen on this menu item (become grandchild extra lines).
      options: {
        name: string;
        priceInCents: number;
        optionGroupId: string | null;
        prepOnly: boolean;
        hideOnPrep: boolean;
      }[];
    }
    const menuChildrenByCartItemId = new Map<string, MenuChildLineSpec[]>();
    // A menu whose price is split over its picks: what its own line carries.
    const menuLinePriceInCentsByCartItemId = new Map<string, number>();

    const menuProductIds = products
      .filter((product) => product.type === ProductTypes.menu)
      .map((product) => product.id);

    if (menuProductIds.length > 0) {
      const [menuCourseItemsResult, menuCourseSettingsResult] =
        await Promise.all([
          supabase
            .from("menu_course_item" as any)
            .select(
              "menu_product_id, course_id, product_id, price_supplement_in_cents"
            )
            .in("menu_product_id", menuProductIds)
            .eq("user_id", userId),
          supabase
            .from("menu_course_setting" as any)
            .select(
              "menu_product_id, course_id, name_override, ticket_suffix, pick_count"
            )
            .in("menu_product_id", menuProductIds)
            .eq("user_id", userId),
        ]);

      if (menuCourseItemsResult.error) {
        console.error(
          "Could not get menu course items",
          menuCourseItemsResult.error
        );
        return { success: false, code: "general_error" };
      }
      if (menuCourseSettingsResult.error) {
        console.error(
          "Could not get menu course settings",
          menuCourseSettingsResult.error
        );
        return { success: false, code: "general_error" };
      }

      const manualMenuCourseItemRows = (menuCourseItemsResult.data ??
        []) as unknown as {
        menu_product_id: string;
        course_id: string;
        product_id: string;
        price_supplement_in_cents: number | null;
      }[];
      // A course also offers the products carrying one of its tags.
      const menuCourseTagItemsResult = await loadMenuCourseTagItemRows(
        supabase,
        userId,
        menuProductIds,
        manualMenuCourseItemRows
      );
      if (menuCourseTagItemsResult.error) {
        console.error(
          "Could not get menu course tag items",
          menuCourseTagItemsResult.error
        );
        return { success: false, code: "general_error" };
      }
      const menuCourseItemRows = [
        ...manualMenuCourseItemRows,
        ...menuCourseTagItemsResult.data.map((row) => ({
          ...row,
          price_supplement_in_cents: 0,
        })),
      ];
      const menuCourseSettingRows = (menuCourseSettingsResult.data ??
        []) as unknown as {
        menu_product_id: string;
        course_id: string;
        name_override: string | null;
        ticket_suffix: string | null;
        pick_count: number | null;
      }[];

      const menuChildProductIds = Array.from(
        new Set(menuCourseItemRows.map((row) => row.product_id))
      );
      const courseIds = Array.from(
        new Set(menuCourseItemRows.map((row) => row.course_id))
      );

      const [menuChildProductsResult, coursesResult] = await Promise.all([
        menuChildProductIds.length > 0
          ? supabase
              .from("product")
              .select(
                "id, name, display_name, preparation_name, status, product_category_id, purchase_price_in_cents, vatRate:vat_rate(value), prices:price_on_product(priceId:price_id, newValue:new_value, oldValue:value), category:product_category(vatRate:vat_rate(value))"
              )
              .in("id", menuChildProductIds)
              .eq("user_id", userId)
              .neq("status", Status.trashed)
          : Promise.resolve({ data: [], error: null }),
        courseIds.length > 0
          ? supabase
              .from("course" as any)
              .select("id, name")
              .in("id", courseIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (menuChildProductsResult.error) {
        console.error(
          "Could not get menu child products",
          menuChildProductsResult.error
        );
        return { success: false, code: "general_error" };
      }

      // The price list this order is priced from: take-away for pickup,
      // delivery otherwise, the same one the menu itself is priced from.
      const orderPriceId =
        data.orderType === OrderTypes.pickup
          ? onlineOrderConfiguration.take_away_price_id
          : onlineOrderConfiguration.delivery_price_id;

      const menuChildProductById = new Map<
        string,
        {
          name: string;
          preparationName: string | null;
          status: string;
          vatRate: number | null;
          categoryId: string | null;
          // Its own price on this order's price list: its weight when a menu's
          // price is split over its picks by VAT.
          unitPriceInCents: number;
          purchasePriceInCents: number | null;
        }
      >();
      (menuChildProductsResult.data as any[]).forEach((child) => {
        const price = (
          (child.prices ?? []) as {
            priceId: string;
            newValue: number | null;
            oldValue: number;
          }[]
        ).find((entry) => entry.priceId === orderPriceId);
        menuChildProductById.set(child.id, {
          name: child.display_name ?? child.name,
          preparationName: child.preparation_name ?? null,
          status: child.status,
          // The item's own rate, then its category's, like the register does.
          vatRate:
            child.vatRate?.value ?? child.category?.vatRate?.value ?? null,
          categoryId: child.product_category_id ?? null,
          // Selected above but never carried over, so every menu item on a
          // website order booked without a cost.
          purchasePriceInCents: child.purchase_price_in_cents ?? null,
          unitPriceInCents: price?.newValue
            ? price.newValue
            : new Decimal(price?.oldValue ?? 0)
                .mul(100)
                .toDecimalPlaces(0)
                .toNumber(),
        });
      });

      const courseNameById = new Map<string, string>();
      ((coursesResult.data ?? []) as any[]).forEach((course) => {
        courseNameById.set(course.id, course.name);
      });

      const nameOverrideByMenuAndCourse = new Map<string, string>();
      const ticketSuffixByMenuAndCourse = new Map<string, string | null>();
      // How many picks a course takes per menu; a course without a setting
      // row takes one.
      const pickCountByMenuAndCourse = new Map<string, number>();
      menuCourseSettingRows.forEach((row) => {
        ticketSuffixByMenuAndCourse.set(
          `${row.menu_product_id}:${row.course_id}`,
          row.ticket_suffix
        );
        pickCountByMenuAndCourse.set(
          `${row.menu_product_id}:${row.course_id}`,
          normalizePickCount(row.pick_count)
        );
        if (row.name_override) {
          nameOverrideByMenuAndCourse.set(
            `${row.menu_product_id}:${row.course_id}`,
            row.name_override
          );
        }
      });

      // Valid (menu, course, product) combinations with their supplement.
      const menuItemByKey = new Map<string, number>();
      menuCourseItemRows.forEach((row) => {
        menuItemByKey.set(
          `${row.menu_product_id}:${row.course_id}:${row.product_id}`,
          row.price_supplement_in_cents ?? 0
        );
      });

      const menuProductIdSet = new Set(menuProductIds);

      data.items.forEach((item) => {
        if (!menuProductIdSet.has(item.productId)) return;
        const picksByCourse = getMenuPicks(item);

        const menuProduct = productsMap.get(item.productId);
        const menuVatRate =
          menuProduct?.vatRate?.value ??
          menuProduct?.category?.vatRate?.value ??
          0;

        const specs: MenuChildLineSpec[] = [];
        const unitPriceInCentsBySpec: number[] = [];
        for (const [courseId, coursePicks] of Object.entries(picksByCourse)) {
          // Only picks that match a real, active item of this course count,
          // and never more than the course takes: anything else is ignored.
          const offeredPicks = selectOfferedMenuPicks(
            coursePicks,
            pickCountByMenuAndCourse.get(`${item.productId}:${courseId}`) ?? 1,
            (pick) =>
              menuItemByKey.has(
                `${item.productId}:${courseId}:${pick.productId}`
              ) &&
              menuChildProductById.get(pick.productId)?.status ===
                Status.active
          );
          // Every pick is its own order line, so 2x the same soup is two.
          for (const pick of offeredPicks) {
            const childProductId = pick.productId;
            const supplement =
              menuItemByKey.get(
                `${item.productId}:${courseId}:${childProductId}`
              ) ?? 0;
            const child = menuChildProductById.get(childProductId);
            if (!child) continue;

            // Resolve any options chosen on this menu item into priced lines,
            // using the same cascade as top-level product options.
            const options = resolveSelectedOptionLines(
              childProductId,
              child.categoryId,
              pick.optionSelections
            ).map((option) => ({
              name: option.productOptionName,
              priceInCents: option.productOptionPrice,
              optionGroupId: option.optionGroupId,
              prepOnly: option.prepOnly,
              hideOnPrep: option.hideOnPrep,
            }));

            specs.push({
              productId: childProductId,
              name: child.name,
              preparationName:
                menuItemPreparationName({
                  preparationName: child.preparationName,
                  name: child.name,
                  ticketSuffix: ticketSuffixByMenuAndCourse.get(
                    `${item.productId}:${courseId}`
                  ),
                }) ?? null,
              courseId,
              courseName:
                nameOverrideByMenuAndCourse.get(
                  `${item.productId}:${courseId}`
                ) ??
                courseNameById.get(courseId) ??
                null,
              priceSupplementInCents: supplement,
              vatRate: child.vatRate ?? menuVatRate,
              menuPriceShareInCents: null,
              purchasePriceInCents: child.purchasePriceInCents,
              options,
            });
            unitPriceInCentsBySpec.push(child.unitPriceInCents);
          }
        }

        // A menu with "split the menu price by VAT" on moves its price onto
        // the picked items, in proportion to their own prices, each share at
        // the item's own VAT rate. The menu line itself then carries nothing,
        // exactly as on the register. The order total does not change.
        // Read loosely: this app's schema snapshot predates columns the
        // products select names, so the row type does not resolve here.
        const splitByVat = (
          menuProduct as { split_menu_price_by_vat?: boolean } | undefined
        )?.split_menu_price_by_vat;
        const menuEntry = productMap.get(item.id);
        if (splitByVat && menuEntry) {
          const split = splitMenuPrice(
            new Decimal(menuEntry.price())
              .mul(100)
              .toDecimalPlaces(0)
              .toNumber(),
            unitPriceInCentsBySpec.map((unitPriceInCents, index) => ({
              key: String(index),
              unitPriceInCents,
            }))
          );
          if (split.isSplit) {
            specs.forEach((spec, index) => {
              spec.menuPriceShareInCents =
                split.lines[index]?.shareInCents ?? 0;
            });
            menuLinePriceInCentsByCartItemId.set(
              item.id,
              split.menuLineValueInCents
            );
          }
        }

        if (specs.length > 0) {
          menuChildrenByCartItemId.set(item.id, specs);
        }
      });
    }

    const menuExtrasTotalInCents = Array.from(
      menuChildrenByCartItemId.entries()
    ).reduce((acc, [cartItemId, specs]) => {
      const quantity = productMap.get(cartItemId)?.quantity ?? 0;
      return (
        acc +
        specs.reduce((sum, spec) => {
          const optionsCents = spec.options.reduce(
            (optSum, option) => optSum + option.priceInCents,
            0
          );
          return sum + (spec.priceSupplementInCents + optionsCents) * quantity;
        }, 0)
      );
    }, 0);

    const productsTotal = Array.from(productMap.values()).reduce(
      (acc, { quantity, price }) => acc + price() * quantity,
      0
    );

    let deliveryFeeInCents = 0;

    const productsTotalInCents = new Decimal(productsTotal)
      .mul(100)
      .toDecimalPlaces(0)
      .toNumber();

    // Calculate total cost of all selected options
    const optionsTotalInCents = Array.from(productMap.values()).reduce(
      (acc, entry) => {
        const optionsCost = entry.selectedOptions.reduce(
          (sum, option) => sum + option.productOptionPrice * entry.quantity,
          0
        );
        return acc + optionsCost;
      },
      0
    );

    let orderTotalInCents =
      productsTotalInCents + optionsTotalInCents + menuExtrasTotalInCents;

    let latitude: number | undefined;
    let longitude: number | undefined;

    let shippingMethod: ShippingMethod | null = null;

    if (isStorefront) {
      const { data: shippingMethodRow, error: shippingMethodError } =
        await supabase
          .from("shipping_method")
          .select(
            "id, name, description, type, price_in_cents, free_from_in_cents, min_order_in_cents, countries, min_delivery_days, max_delivery_days"
          )
          .eq("id", data.shippingMethodId!)
          .eq("user_id", userId)
          .eq("status", Status.active)
          .maybeSingle();

      if (shippingMethodError) {
        console.error("Could not get shipping method", shippingMethodError);
        return { success: false, code: "general_error" };
      }

      shippingMethod = shippingMethodRow
        ? toShippingMethod(shippingMethodRow)
        : null;

      // The method's type decides how the order leaves the shop; the client's
      // order type must agree with it.
      const expectedOrderType =
        shippingMethod?.type === ShippingMethodTypes.pickup
          ? OrderTypes.pickup
          : OrderTypes.delivery;

      if (!shippingMethod || data.orderType !== expectedOrderType) {
        return { success: false, code: "shipping_method_unavailable" };
      }

      if (!shipsToCountry(shippingMethod, data.address?.country)) {
        return { success: false, code: "shipping_country_not_supported" };
      }

      if (!meetsMinimumOrder(shippingMethod, orderTotalInCents)) {
        return {
          success: false,
          code: "min_order_amount_not_met",
          minOrderAmount: new Decimal(shippingMethod.minOrderInCents)
            .div(100)
            .toNumber(),
        };
      }

      deliveryFeeInCents = getShippingFeeInCents(
        shippingMethod,
        orderTotalInCents
      );
      orderTotalInCents += deliveryFeeInCents;
    }

    if (!isStorefront && data.orderType === OrderTypes.delivery && data.address) {
      const zipCodeWithoutLetters = data.address.zipCode.replace(
        /[a-zA-Z ]/g,
        ""
      );

      const { data: deliveryArea, error: deliveryAreayError } = await supabase
        .from("delivery_area")
        .select(
          "id, minOrderAmount:min_order_amount, free_delivery_from, fee, zipCodes:zip_code_to_delivery_area!inner(zipCode:zip_code)"
        )
        .in("zip_code_to_delivery_area.zip_code", [zipCodeWithoutLetters])
        .eq("zip_code_to_delivery_area.status", Status.active)
        .eq("user_id", userId)
        .eq("status", Status.active)
        .limit(1)
        .maybeSingle();

      if (deliveryAreayError) {
        console.error("Could not get delivery area", deliveryAreayError);
        throw new Error("Could not get delivery area");
      }

      if (!deliveryArea) {
        return { success: false, code: "address_not_in_delivery_area" };
      }

      // Server-side guard: reject delivery to an area the staff have closed for
      // today, even if a stale client got past the up-front availability check.
      const { data: deliveryAreaBlackout, error: deliveryAreaBlackoutError } =
        await supabase
          .from("delivery_area_blackout_date")
          .select("id")
          .eq("user_id", userId)
          .eq("delivery_area_id", deliveryArea.id)
          .eq("date", todayDateString)
          .limit(1)
          .maybeSingle();

      if (deliveryAreaBlackoutError) {
        console.error(
          "Could not check delivery area blackout",
          deliveryAreaBlackoutError
        );
        throw new Error("Could not check delivery area blackout");
      }

      if (deliveryAreaBlackout) {
        return { success: false, code: "delivery_area_unavailable_today" };
      }

      if (productsTotalInCents < deliveryArea.minOrderAmount) {
        return {
          success: false,
          code: "min_order_amount_not_met",
          minOrderAmount: new Decimal(deliveryArea.minOrderAmount)
            .div(100)
            .toNumber(),
        };
      }

      if (deliveryArea.free_delivery_from) {
        if (orderTotalInCents >= deliveryArea.free_delivery_from) {
          deliveryFeeInCents = 0;
        } else {
          deliveryFeeInCents = deliveryArea.fee;
        }
      } else {
        deliveryFeeInCents = deliveryArea.fee;
      }

      orderTotalInCents += deliveryFeeInCents;

      const address = await getAddress({
        data: {
          streetNumber: data.address.streetNumber,
          postalCode: data.address.zipCode,
          sessionId: data.sessionId ?? "",
        },
      });
      latitude = address.latitude;
      longitude = address.longitude;
    }

    const {
      data: paymentMethodAvailability,
      error: paymentMethodAvailabilityError,
    } = await supabase
      .from("payment_method_on_user")
      .select(
        "id, name, context, fee, fee_percentage, paymentMethod:payment_method_new(type, name)"
      )
      .eq("id", data.payment.paymentMethodId)
      .eq("user_id", userId)
      .single();

    if (paymentMethodAvailabilityError) {
      console.error(
        "Could not get payment method",
        paymentMethodAvailabilityError
      );
      return {
        success: false,
        code: "general_error",
      };
    }

    if (!paymentMethodAvailability.paymentMethod) {
      console.error("Payment method data is missing");
      return {
        success: false,
        code: "general_error",
      };
    }

    const paymentMethod = paymentMethodAvailability.paymentMethod;

    // Cash and card are settled in person at pickup/delivery, so the order
    // is confirmed immediately instead of waiting for an online payment.
    const isPaidInPerson =
      paymentMethod.type === PaymentMethods.cash ||
      paymentMethod.type === PaymentMethods.card;

    // A webshop order is paid when it is placed: stock is taken off and the
    // parcel prepared on the payment, never on a promise to pay later.
    if (
      isStorefront &&
      (paymentMethodAvailability.context !== PaymentMethodContext.webshop ||
        (paymentMethod.type !== PaymentMethods.ideal &&
          paymentMethod.type !== PaymentMethods.applepay &&
          paymentMethod.type !== PaymentMethods.creditcard))
    ) {
      return { success: false, code: "payment_method_not_available" };
    }

    // The percentage is charged over the order as it stands here — products,
    // options and the delivery fee — which is exactly the total the checkout
    // page shows the fee under, so the guest is never charged a different
    // amount than they agreed to. The coupon discount is settled afterwards
    // and deliberately left out of the base.
    const transactionFeeInCents = calculatePaymentMethodFeeInCents({
      fixedFeeInCents: paymentMethodAvailability.fee,
      feePercentage: paymentMethodAvailability.fee_percentage / 100,
      baseAmountInCents: orderTotalInCents,
    });
    orderTotalInCents += transactionFeeInCents;

    // check if the customer already has an account
    const { data: dbCustomer, error: dbCustomerError } = await supabase
      .from("customer")
      .upsert(
        {
          email: data.customer.email,
        },
        {
          onConflict: "email",
        }
      )
      .select("id")
      .single();

    if (dbCustomerError) {
      console.error("Could not get customer", dbCustomerError);
      return {
        success: false,
        code: "general_error",
      };
    }

    const { error: dbAccountError } = await supabase.from("account").upsert(
      {
        customer_id: dbCustomer.id,
        user_id: userId,
        first_name: data.customer.firstName,
        last_name: data.customer.lastName,
        phone: data.customer.phone,
        street_name: data.address?.streetName,
        street_number: data.address?.streetNumber,
        zip_code: data.address?.zipCode,
        city: data.address?.city,
      },
      {
        onConflict: "customer_id,user_id",
      }
    );

    if (dbAccountError) {
      console.error("Could not get account", dbAccountError);
      return {
        success: false,
        code: "general_error",
      };
    }

    // All products are active, now we can create the order
    const { data: newOrder, error: newOrderError } = await supabase
      .from("order")
      .insert({
        type: data.orderType,
        user_id: userId,
        payment_status: isPaidInPerson ? "paid" : "pending",
        // Set explicitly rather than relying on the column default: a new
        // order is never finalized, and every other order-insert path sets
        // this. Omitting it here inherited a stale is_finalized=true default,
        // which finalized own_website orders at creation and hid them from the
        // unpaid takeaway/delivery list.
        is_finalized: false,
        secret: generatedSecret,
        reference: generatedReference,
        customer_id: dbCustomer.id,
        provider: "own_website",
      })
      .select("id, user(language)")
      .single();

    if (newOrderError) {
      console.error("Could not create order", newOrderError);
      return {
        success: false,
        code: "general_error",
      };
    }

    // Every line on an incoming online order is fired the moment the order is
    // placed — it never sits in the POS as an item still waiting to be sent to
    // the kitchen. Stamp ordered_at alongside the implicit ordered=true (the
    // column default) so the register groups these items under their order
    // time; lines without an ordered_at land under an "Unordered" heading in
    // the cart, which also exposes a prep-ticket reprint button on an order
    // that already had its online order ticket. Mirrors the Thuisbezorgd
    // ingestion, which stamps the platform order date.
    const orderedAt = new Date().toISOString();

    // Prepare main product order lines
    const productOrderLines = Array.from(productMap.values()).map(
      ({
        product,
        orderItem,
        quantity,
        price,
        categoryName,
        turnoverGroupName,
        vatRate,
      }) => {
        // A menu split by VAT leaves its price to its picks.
        const splitMenuLinePriceInCents = menuLinePriceInCentsByCartItemId.get(
          orderItem.id
        );
        const unitPriceInCents =
          splitMenuLinePriceInCents ??
          new Decimal(price()).mul(100).toDecimalPlaces(0).toNumber();
        return {
          order_id: newOrder.id,
          user_id: userId,
          // Links the line back to the POS product so the POS can resolve its
          // preparation routing (prep tickets / KDS) on acceptance.
          product_id: product.id,
          name: product.name,
          display_name: product.display_name,
          price: new Decimal(unitPriceInCents).div(100).toNumber(),
          new_price: unitPriceInCents,
          quantity: quantity,
          vat_rate: vatRate,
          category_name: categoryName,
          turnover_group_name: turnoverGroupName,
          type: OrderLineTypes.product,
          ordered_at: orderedAt,
          // Cost at the moment of sale, so an online order reports the same
          // margin the POS would and a later supplier price change cannot
          // rewrite it. null when the merchant tracks no cost for this product.
          purchase_price_in_cents: product.purchase_price_in_cents ?? null,
        };
      }
    );

    const transactionFeeOrderLine =
      transactionFeeInCents > 0
        ? [
            {
              order_id: newOrder.id,
              user_id: userId,
              name: "Transaction fee",
              price: new Decimal(transactionFeeInCents).div(100).toNumber(),
              new_price: transactionFeeInCents,
              quantity: 1,
              vat_rate: 21,
              category_name: "Fees",
              turnover_group_name: "Fees",
              type: OrderLineTypes.transaction_fee,
              ordered_at: orderedAt,
            },
          ]
        : [];

    // Add delivery fee if applicable
    const deliveryFeeOrderLine =
      deliveryFeeInCents > 0
        ? [
            {
              order_id: newOrder.id,
              user_id: userId,
              name: shippingMethod?.name ?? "Delivery fee",
              price: new Decimal(deliveryFeeInCents).div(100).toNumber(),
              new_price: deliveryFeeInCents,
              quantity: 1,
              vat_rate: 21,
              category_name: "Fees",
              turnover_group_name: "Fees",
              type: OrderLineTypes.delivery_fee,
              ordered_at: orderedAt,
            },
          ]
        : [];

    // Insert the main order items and get their IDs back
    const { data: insertedOrderLines, error: orderLinesError } = await supabase
      .from("order_line")
      .insert([
        ...productOrderLines,
        ...deliveryFeeOrderLine,
        ...transactionFeeOrderLine,
      ])
      .select("id");

    if (orderLinesError) {
      console.error("Could not create order lines", orderLinesError);
      return {
        success: false,
        code: "general_error",
      };
    }

    if (!insertedOrderLines) {
      console.error("No order lines were inserted");
      return {
        success: false,
        code: "general_error",
      };
    }

    // Map productMap entries to their inserted order line IDs
    // The order of insertedOrderLines matches the order of productOrderLines
    const productMapEntries = Array.from(productMap.values());
    const orderLineIdMap = new Map<string, string>();
    for (let i = 0; i < productMapEntries.length; i++) {
      const entry = productMapEntries[i];
      const orderLineId = insertedOrderLines[i]?.id;
      if (orderLineId) {
        orderLineIdMap.set(entry.orderItem.id, orderLineId);
      }
    }

    // Insert selected options as order lines with parent_id
    const optionOrderLines: Array<{
      order_id: string;
      user_id: string;
      name: string;
      price: number;
      new_price: number;
      quantity: number;
      vat_rate: number;
      category_name?: string;
      turnover_group_name?: string;
      parent_id: string;
      type: "extra";
      ordered_at: string;
      v2_option_group_id: string | null;
      prep_only: boolean;
      hide_on_prep: boolean;
    }> = [];

    for (const entry of productMapEntries) {
      const parentOrderLineId = orderLineIdMap.get(entry.orderItem.id);
      if (!parentOrderLineId) continue;

      for (const selectedOption of entry.selectedOptions) {
        const optionPriceInEuros = new Decimal(
          selectedOption.productOptionPrice
        )
          .div(100)
          .toNumber();

        optionOrderLines.push({
          order_id: newOrder.id,
          user_id: userId,
          name: selectedOption.productOptionName,
          price: optionPriceInEuros,
          new_price: selectedOption.productOptionPrice,
          quantity: entry.quantity, // Options have the same quantity as the parent item
          vat_rate: entry.vatRate, // Use the same VAT rate as the parent product
          category_name: entry.categoryName,
          turnover_group_name: entry.turnoverGroupName,
          parent_id: parentOrderLineId,
          type: OrderLineTypes.extra,
          ordered_at: orderedAt,
          // Colors the extra on the kitchen display.
          v2_option_group_id: selectedOption.optionGroupId,
          // Keeps a kitchen-only option off the receipt.
          prep_only: selectedOption.prepOnly,
          // Keeps a receipt-only option off the kitchen display.
          hide_on_prep: selectedOption.hideOnPrep,
        });
      }
    }

    // Insert option order lines if any exist
    if (optionOrderLines.length > 0) {
      const { error: optionOrderLinesError } = await supabase
        .from("order_line")
        .insert(optionOrderLines);

      if (optionOrderLinesError) {
        console.error(
          "Could not create option order lines",
          optionOrderLinesError
        );
        return {
          success: false,
          code: "general_error",
        };
      }
    }

    // Insert menu course items as child order lines under their menu line. Each
    // child carries only the per-item supplement (the base price stays on the
    // parent menu line) plus its course id/name so the POS can render the menu
    // structure. Uses type "product" with product_id — mirroring the POS's own
    // menu lines — so the POS routes each item to its preparation station.
    const menuChildOrderLines: Array<{
      order_id: string;
      user_id: string;
      product_id: string;
      name: string;
      preparation_name: string | null;
      price: number;
      new_price: number;
      quantity: number;
      vat_rate: number;
      category_name?: string;
      turnover_group_name?: string;
      parent_id: string;
      course_id: string;
      course_name: string | null;
      type: "product";
      ordered_at: string;
      menu_price_share_in_cents: number | null;
    }> = [];
    // Parallel to menuChildOrderLines (same order) so each menu item's chosen
    // options can be attached as grandchild lines once we know the inserted ids.
    const menuChildLineSpecs: Array<{
      spec: MenuChildLineSpec;
      quantity: number;
      categoryName?: string;
      turnoverGroupName?: string;
    }> = [];

    for (const [cartItemId, specs] of menuChildrenByCartItemId.entries()) {
      const parentOrderLineId = orderLineIdMap.get(cartItemId);
      if (!parentOrderLineId) continue;
      const parentEntry = productMap.get(cartItemId);
      const quantity = parentEntry?.quantity ?? 1;

      for (const spec of specs) {
        menuChildOrderLines.push({
          order_id: newOrder.id,
          user_id: userId,
          product_id: spec.productId,
          name: spec.name,
          preparation_name: spec.preparationName,
          price: new Decimal(
            spec.priceSupplementInCents + (spec.menuPriceShareInCents ?? 0)
          )
            .div(100)
            .toNumber(),
          new_price:
            spec.priceSupplementInCents + (spec.menuPriceShareInCents ?? 0),
          menu_price_share_in_cents: spec.menuPriceShareInCents,
          quantity,
          vat_rate: spec.vatRate,
          category_name: parentEntry?.categoryName,
          turnover_group_name: parentEntry?.turnoverGroupName,
          parent_id: parentOrderLineId,
          course_id: spec.courseId,
          course_name: spec.courseName,
          type: OrderLineTypes.product,
          ordered_at: orderedAt,
          purchase_price_in_cents: spec.purchasePriceInCents,
        });
        menuChildLineSpecs.push({
          spec,
          quantity,
          categoryName: parentEntry?.categoryName,
          turnoverGroupName: parentEntry?.turnoverGroupName,
        });
      }
    }

    if (menuChildOrderLines.length > 0) {
      const { data: insertedMenuChildLines, error: menuChildOrderLinesError } =
        await supabase
          .from("order_line")
          .insert(menuChildOrderLines)
          .select("id");

      if (menuChildOrderLinesError) {
        console.error(
          "Could not create menu child order lines",
          menuChildOrderLinesError
        );
        return {
          success: false,
          code: "general_error",
        };
      }

      // Insert the options chosen on menu items as grandchild extra lines under
      // their course item. Inserted rows come back in insertion order, so they
      // line up with menuChildLineSpecs by index.
      const menuOptionOrderLines: Array<{
        order_id: string;
        user_id: string;
        name: string;
        price: number;
        new_price: number;
        quantity: number;
        vat_rate: number;
        category_name?: string;
        turnover_group_name?: string;
        parent_id: string;
        type: "extra";
        ordered_at: string;
        v2_option_group_id: string | null;
        prep_only: boolean;
        hide_on_prep: boolean;
      }> = [];

      (insertedMenuChildLines ?? []).forEach((line, index) => {
        const info = menuChildLineSpecs[index];
        if (!info || !line?.id) return;
        info.spec.options.forEach((option) => {
          menuOptionOrderLines.push({
            order_id: newOrder.id,
            user_id: userId,
            name: option.name,
            price: new Decimal(option.priceInCents).div(100).toNumber(),
            new_price: option.priceInCents,
            quantity: info.quantity,
            vat_rate: info.spec.vatRate,
            category_name: info.categoryName,
            turnover_group_name: info.turnoverGroupName,
            parent_id: line.id,
            type: OrderLineTypes.extra,
            ordered_at: orderedAt,
            v2_option_group_id: option.optionGroupId,
            prep_only: option.prepOnly,
            hide_on_prep: option.hideOnPrep,
          });
        });
      });

      if (menuOptionOrderLines.length > 0) {
        const { error: menuOptionOrderLinesError } = await supabase
          .from("order_line")
          .insert(menuOptionOrderLines);

        if (menuOptionOrderLinesError) {
          console.error(
            "Could not create menu option order lines",
            menuOptionOrderLinesError
          );
          return {
            success: false,
            code: "general_error",
          };
        }
      }
    }

    const orderLinesWithComments = Array.from(productMap.values())
      .filter(({ orderItem }) => orderItem.comment)
      .map(({ orderItem }) => {
        const orderLineId = orderLineIdMap.get(orderItem.id);
        if (!orderLineId) {
          return null;
        }
        return {
          order_id: newOrder.id,
          order_line_id: orderLineId,
          value: orderItem.comment,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const { error } = await supabase.from("comment_line").insert(
      orderLinesWithComments.map((orderLineWithComment) => ({
        order_id: orderLineWithComment.order_id,
        order_line_id: orderLineWithComment.order_line_id,
        value: orderLineWithComment.value ?? "",
        ordered: true,
      }))
    );

    if (data.comment) {
      const { error: orderCommentError } = await supabase
        .from("comment_line")
        .insert({
          order_id: newOrder.id,
          value: data.comment,
          ordered: true,
        });

      if (orderCommentError) {
        console.error("Could not add order comment", orderCommentError);
        return {
          success: false,
          code: "general_error",
        };
      }
    }

    if (error) {
      console.error("Could not add item comment", error);
      return {
        success: false,
        code: "general_error",
      };
    }

    let appliedDiscountInCents = 0;
    let appliedCouponId: string | undefined;

    if (data.coupon) {
      const couponResult = await checkCoupon({
        data: {
          code: data.coupon.code,
          // A coupon limited to other channels does not count on the webshop.
          ...(isStorefront && { context: CouponContext.webshop }),
        },
      });
      if (!couponResult.success) {
        return {
          success: false,
          code: "coupon_error",
        };
      }

      if (!couponResult.coupon) {
        return {
          success: false,
          code: "coupon_error",
        };
      }

      if (couponResult.coupon.type === "cart_adjustment") {
        appliedDiscountInCents = await calculateOrderDiscountAllocations({
          orderId: newOrder.id,
          discountValue: couponResult.coupon.discountValue,
          discountType: couponResult.coupon
            .discountType as CouponAdjustmentTypes,
        });

        console.log("appliedDiscount", appliedDiscountInCents);
      } else if (couponResult.coupon.type === "product_adjustment") {
        appliedDiscountInCents = await calculateProductDiscountAllocations({
          orderId: newOrder.id,
          discountValue: couponResult.coupon.discountValue,
          discountType: couponResult.coupon
            .discountType as CouponAdjustmentTypes,
          filterType: couponResult.coupon.filterType as CouponFilters,
          productIds: couponResult.coupon.productIds,
          categoryIds: couponResult.coupon.categoryIds,
          productMap,
        });
      }

      appliedCouponId = couponResult.coupon.id;
    }

    // "Exact amount" must match what the customer actually owes, so subtract
    // the coupon discount like the Mollie payment amount below does. The
    // checkout UI already shows the discounted total for this option.
    const paysWith = data.payment.paysWith
      ? data.payment.paysWith === "exact_amount"
        ? orderTotalInCents - appliedDiscountInCents
        : new Decimal(data.payment.paysWith).mul(100).toNumber()
      : orderTotalInCents - appliedDiscountInCents;

    const { error: newOnlineOrderError } = await supabase
      .from("online_order")
      .insert({
        number: isPaidInPerson ? orderNumber : null,
        order_status: isPaidInPerson ? "new" : "pending_payment",
        status: isPaidInPerson ? "active" : "inactive",
        order_type: data.orderType,
        timeslot_time: slotStart.toISOString(),
        order_id: newOrder.id,
        platform: "own_website",
        is_paid: false,
        pays_with_in_cents: paysWith,
        // What the customer owes: products, options, menu supplements, the
        // delivery and transaction fees, minus the coupon. This is the number
        // the Mollie payment is created for and the receipt totals to, and it
        // feeds every surface that reads the row instead of the order lines
        // (status emails, the customer profile, the caller lookup). It used to
        // store the bare product prices, so an order with a paid option or a
        // transaction fee emailed a total lower than the receipt.
        total_price_in_cents: orderTotalInCents - appliedDiscountInCents,
        delivery_fee_in_cents: deliveryFeeInCents,
        transaction_fee_in_cents: transactionFeeInCents,
        total_discount_in_cents: appliedDiscountInCents,
        user_id: userId,
        first_name: data.customer.firstName,
        last_name: data.customer.lastName,
        phone: data.customer.phone,
        requested_time: data.requestedTime ?? null,
        street_name: data.address?.streetName,
        street_number: data.address?.streetNumber,
        zip_code: data.address?.zipCode,
        city: data.address?.city,
        latitude: latitude,
        longitude: longitude,
        language: data.language,
        channel: isStorefront
          ? SalesChannels.storefront
          : SalesChannels.ordering,
        online_order_configuration_id: onlineOrderConfigurationId,
        ...(isStorefront && {
          shipping_method_id: shippingMethod?.id ?? null,
          shipping_method_name: shippingMethod?.name ?? null,
          country: data.address?.country ?? null,
          address_addition: data.address?.addition || null,
        }),
      });

    if (newOnlineOrderError) {
      console.error("Could not create online order", newOnlineOrderError);
      return {
        success: false,
        code: "general_error",
      };
    }

    if (appliedCouponId) {
      await linkCouponToOnlineOrder({
        supabase,
        userId,
        orderId: newOrder.id,
        couponId: appliedCouponId,
      });
    }

    if (
      paymentMethod.type === PaymentMethods.ideal ||
      paymentMethod.type === PaymentMethods.applepay ||
      paymentMethod.type === PaymentMethods.creditcard
    ) {
      const mollieRefreshResult = await mollie.refreshMollieToken({
        userId,
      });

      if (!mollieRefreshResult.accessToken) {
        console.error(
          "Could not refresh mollie token",
          mollieRefreshResult.code
        );
        await supabase
          .from("order")
          .update({
            status: "trashed",
          })
          .eq("id", newOrder.id);
        return {
          success: false,
          code: mollieRefreshResult.code,
        };
      }

      if (!onlineOrderConfiguration.mollieProfileId) {
        console.error("No mollie profile id found");
        return {
          success: false,
          code: "no_mollie_profile_id",
        };
      }

      const accessToken = mollieRefreshResult.accessToken;
      const profileId = onlineOrderConfiguration.mollieProfileId;

      const { data: payment, error: paymentError } = await supabase
        .from("payment")
        .insert({
          user_id: userId,
          amount: (orderTotalInCents - appliedDiscountInCents) / 100,
          new_amount: orderTotalInCents - appliedDiscountInCents,
          currency: "EUR",
          idempotency: generateRandomString(7),
          order_id: newOrder.id,
          testmode: process.env.ENVIRONMENT === "development",
          payment_method_name:
            paymentMethodAvailability.name ?? paymentMethod.name,
        })
        .select("id")
        .single();

      if (paymentError) {
        console.error(paymentError);
        return {
          success: false,
          paymentUrl: null,
          order: null,
          code: "general_error",
        };
      }

      try {
        // The order page only exists at `/$locale/order/...`. Land Mollie's
        // return straight on the real route: omitting the locale forces the
        // request through the `$locale` catch-all redirect (locale="order"),
        // which is the only thing re-attaching `?secret=` and can drop it,
        // leaving the customer on an order page with no secret. Clamp to a
        // locale the app actually routes (data.language may be fr/de/it).
        const redirectLocale = supportedLanguages.includes(data.language)
          ? data.language
          : "en";

        // create mollie payment
        const mollieClient = createMollieClient({ accessToken });
        const molliePayment = await mollieClient.payments.create({
          amount: {
            value: formatNumberToCurrency({
              number: new Decimal(orderTotalInCents - appliedDiscountInCents)
                .div(100)
                .toDecimalPlaces(2)
                .toNumber(),
              showCurrency: false,
              locale: "en-US",
            }),
            currency: "EUR",
          },
          method:
            paymentMethod.type === PaymentMethods.ideal
              ? PaymentMethod.ideal
              : paymentMethod.type === PaymentMethods.applepay
              ? PaymentMethod.applepay
              : PaymentMethod.creditcard,
          description: `Vrodex Online Order #${generatedReference}`,
          redirectUrl: `https://${origin}/${redirectLocale}/order/${generatedReference}?secret=${generatedSecret}`,
          cancelUrl: `https://${origin}/${redirectLocale}/checkout`,
          webhookUrl: `${process.env.MOLLIE_ONLINE_ORDERS_WEBHOOK_URL}?userId=${userId}`,
          profileId: profileId,
          testmode: process.env.ENVIRONMENT === "development",
          metadata: {
            referenceId: payment.id,
          },
        });

        await supabase
          .from("payment")
          .update({
            ext_transaction_id: molliePayment.id,
          })
          .eq("id", payment.id);

        return {
          success: true,
          paymentUrl: molliePayment.getCheckoutUrl(),
          order: null,
        };
      } catch (error) {
        console.error(error);
        return { success: false, paymentUrl: null, code: "general_error" };
      }
    } else {
      const handleAfter = async () => {
        const { t } = await getServerT(newOrder.user.language);

        // Paid in person, so the order is placed now and the coupon is used.
        await syncCouponUsageForOnlineOrder({
          supabase,
          userId,
          orderId: newOrder.id,
        });

        await sendBroadcast({
          userId,
          event: "new-online-order",
          payload: { orderId: newOrder.id },
        });

        // Cash/card orders are confirmed in person, so there is no payment
        // webhook to trigger the confirmation email — send it here. The order
        // lines carry any applied coupon discount via
        // order_discount_allocation_in_cents, so the email reflects the
        // discounted total just like the online-payment flow.
        if (data.customer.email) {
          try {
            const {
              data: confirmationOrderLines,
              error: confirmationOrderLinesError,
            } = await supabase
              .from("order_line")
              .select(
                "id, parent_id, status, name, quantity, price, type, order_discount_allocation_in_cents"
              )
              .eq("order_id", newOrder.id);

            if (confirmationOrderLinesError) {
              console.error(
                "Could not get order lines for confirmation email",
                confirmationOrderLinesError
              );
            } else {
              await sendOrderConfirmationEmail({
                userId,
                customerEmail: data.customer.email,
                orderReference: generatedReference,
                orderSecret: generatedSecret,
                orderCreatedAt: new Date(),
                language: data.language,
                orderLines: confirmationOrderLines ?? [],
              });
            }
          } catch (e) {
            console.error("Could not send order confirmation email", e);
          }
        }

        const { data: pushTokenDevices, error: pushTokensError } =
          await supabase
            .from("device_on_user")
            .select("push_token")
            .eq("user_id", userId);

        if (pushTokensError) {
          console.error("Could not get push tokens", pushTokensError);
        } else {
          let tokens: string[] = [];
          for (const token of pushTokenDevices) {
            if (token.push_token) {
              tokens = [...tokens, token.push_token];
            }
          }
          try {
            await sendPushNotification({
              pushTokens: tokens,
              title: t("Order.new_order_push_notification_title"),
              message: t("Order.new_order_push_notification_message"),
              data: {
                orderId: newOrder.id,
              },
            });
          } catch (e) {
            console.error(e);
          }
        }
      };

      waitUntil(handleAfter());

      return {
        reference: generatedReference,
        secret: generatedSecret,
      };
    }
  });

export const orderQueryOptions = (reference: string, secret?: string) =>
  queryOptions({
    queryKey: ["order", reference, secret],
    queryFn: () => getOrderByReference({ data: { reference, secret } }),
  });

export const getOrderByReference = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(
    z.object({ reference: z.string(), secret: z.string().optional() })
  )
  .handler(async ({ context: { userId }, data }) => {
    if (!data.secret) {
      throw new Error("Secret is required");
    }

    const supabase = createClient();

    const { data: order, error: orderError } = await supabase
      .from("online_order")
      .select(
        "confirmedTime:confirmed_time, orderStatus:order_status, orderType:order_type, deliveryLatitude:latitude, deliveryLongitude:longitude, channel, shippingMethodName:shipping_method_name, trackingCode:tracking_code, trackingUrl:tracking_url, shippedAt:shipped_at, streetName:street_name, streetNumber:street_number, addressAddition:address_addition, zipCode:zip_code, city, country, order!inner(reference, secret, items:order_line(name, quantity, price, type, order_discount_allocation_in_cents))"
      )
      .eq("order.reference", data.reference)
      .eq("order.secret", data.secret)
      .eq("user_id", userId)
      .maybeSingle();

    if (orderError) {
      console.error("Could not get order", orderError);
      throw new Error("Could not get order");
    }

    if (!order) {
      throw new Error("Order not found");
    }

    const orderLines = (order.order?.items ?? []) as Array<{
      name: string;
      quantity: number;
      price: number;
      type?: string;
      order_discount_allocation_in_cents?: number;
    }>;
    const { items, fees, discount } = buildOrderConfirmationLineData(
      orderLines,
      "Discount"
    );
    const itemsSubtotal = items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    const feesTotal = (fees ?? []).reduce((sum, f) => sum + f.amount, 0);
    const total = itemsSubtotal + feesTotal - (discount?.amount ?? 0);

    return {
      confirmedTime: order.confirmedTime ? parseISO(order.confirmedTime) : null,
      status: order.orderStatus,
      orderType: order.orderType,
      deliveryLatitude: order.deliveryLatitude ?? undefined,
      deliveryLongitude: order.deliveryLongitude ?? undefined,
      // Storefront orders: how the parcel travels and where to follow it.
      channel: order.channel,
      shipping:
        order.channel === SalesChannels.storefront
          ? {
              methodName: order.shippingMethodName,
              trackingCode: order.trackingCode,
              // Only ever rendered as a link, so only http(s) gets through.
              trackingUrl:
                order.trackingUrl && /^https?:\/\//i.test(order.trackingUrl)
                  ? order.trackingUrl
                  : null,
              shippedAt: order.shippedAt ? parseISO(order.shippedAt) : null,
              address:
                order.orderType === OrderTypes.delivery
                  ? {
                      streetName: order.streetName,
                      streetNumber: order.streetNumber,
                      addition: order.addressAddition,
                      zipCode: order.zipCode,
                      city: order.city,
                      country: order.country,
                    }
                  : null,
            }
          : null,
      summary: {
        items,
        fees,
        discount,
        subtotal: itemsSubtotal,
        total,
      },
    };
  });

export type OrderByReference = Awaited<ReturnType<typeof getOrderByReference>>;
